import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  ArrowBack,
  Person,
  SportsSoccer,
  LocationOn,
  Schedule,
  AttachMoney,
  Phone,
  Email,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI } from '../services/api';

const BookingDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: 'confirm',
    reason: ''
  });
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (id) {
      fetchBookingDetails();
    }
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getById(id);
      setBooking(response.data.data);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setAlert({ type: 'error', message: 'Failed to load booking details' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus, reason) => {
    if (!booking) return;

    try {
      if (newStatus === 'cancelled') {
        await bookingsAPI.cancel(booking.id, reason);
      } else {
        await bookingsAPI.updateStatus(booking.id, newStatus);
      }

      setAlert({
        type: 'success',
        message: `Booking ${newStatus} successfully`
      });

      // Refresh booking details
      fetchBookingDetails();
    } catch (error) {
      console.error('Error updating booking:', error);
      setAlert({ type: 'error', message: 'Failed to update booking' });
    }

    setConfirmDialog({ open: false, action: 'confirm', reason: '' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return 'success';
      case 'requested':
      case 'pending_on_arrival':
        return 'warning';
      case 'cancelled':
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'paid':
        return <CheckCircle />;
      case 'requested':
      case 'pending_on_arrival':
        return <Schedule />;
      case 'cancelled':
      case 'no_show':
        return <Cancel />;
      default:
        return undefined;
    }
  };

  const formatDateTime = (date, startTime, endTime) => {
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return {
      date: formattedDate,
      time: `${startTime} - ${endTime}`,
      duration: calculateDuration(startTime, endTime)
    };
  };

  const calculateDuration = (start, end) => {
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    const durationMinutes = endMinutes - startMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading booking details...</Typography>
      </Box>
    );
  }

  if (!booking) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Booking not found
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/bookings')}
          sx={{ mt: 2 }}
        >
          Back to Bookings
        </Button>
      </Box>
    );
  }

  const dateTimeInfo = formatDateTime(booking.date, booking.start_time, booking.end_time);

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          onClick={() => navigate('/bookings')}
          sx={{ cursor: 'pointer' }}
        >
          Bookings
        </Link>
        <Typography color="text.primary">{booking.id}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Booking {booking.id}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              icon={getStatusIcon(booking.status)}
              label={booking.status.toUpperCase()}
              color={getStatusColor(booking.status)}
              size="medium"
            />
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(booking.created_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/bookings')}
          >
            Back
          </Button>
          {booking.status === 'requested' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => setConfirmDialog({ open: true, action: 'confirm', reason: '' })}
              >
                Confirm Booking
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setConfirmDialog({ open: true, action: 'cancel', reason: '' })}
              >
                Cancel Booking
              </Button>
            </>
          )}
        </Box>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Booking Details */}
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {/* Booking Information */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SportsSoccer />
                Booking Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <SportsSoccer color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Sport
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {booking.sport_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <LocationOn color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Court
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {booking.court_label}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Schedule color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {dateTimeInfo.date}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Schedule color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Time
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {dateTimeInfo.time} ({dateTimeInfo.duration})
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Person color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Players
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {booking.players_count} players
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <AttachMoney color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        ₹{booking.amount}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {booking.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {booking.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* User Information */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person />
                Customer Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Name
                </Typography>
                <Typography variant="body1" fontWeight="medium" gutterBottom>
                  {booking.user_name}
                </Typography>
              </Box>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Phone
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body1">
                    {booking.user_phone}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Payment Status
                </Typography>
                <Chip
                  label={booking.payment_status || 'N/A'}
                  color={booking.payment_status === 'paid' ? 'success' : 'warning'}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: 'confirm', reason: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'confirm' ? 'Confirm Booking' : 'Cancel Booking'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to {confirmDialog.action} booking {booking.id}?
          </Typography>

          {confirmDialog.action === 'cancel' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Cancellation Reason (Optional)"
              value={confirmDialog.reason}
              onChange={(e) => setConfirmDialog(prev => ({ ...prev, reason: e.target.value }))}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: 'confirm', reason: '' })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleStatusChange(
              confirmDialog.action === 'confirm' ? 'confirmed' : 'cancelled',
              confirmDialog.reason
            )}
            variant="contained"
            color={confirmDialog.action === 'confirm' ? 'success' : 'error'}
          >
            {confirmDialog.action === 'confirm' ? 'Confirm Booking' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingDetails;